import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Free Call',
    tagline: 'Revenue Optimization Consultation',
    price: 'FREE',
    priceNote: '15-minute consultation',
    additionalRate: 'No obligation',
    additionalNote: 'See what we can do for you',
    phoneHours: '15 minutes included',
    features: [
      'Quick property revenue assessment',
      'Market rate analysis for your area',
      'Pricing strategy recommendations',
      'Platform optimization tips',
      'Occupancy improvement suggestions',
      'Answer your burning questions',
      'Personalized package recommendation',
      'No commitment required'
    ],
    popular: false,
    color: 'bg-gradient-to-br from-[#FF6B6B] to-[#ff5252]',
    isFree: true
  },
  {
    name: 'Silver',
    tagline: 'Perfect for DIY Hosts',
    price: '$299',
    priceNote: 'One-time setup fee',
    additionalRate: '$199/hour',
    additionalNote: 'Additional phone service',
    phoneHours: '1 hour included',
    features: [
      'General host training program',
      'Complete property evaluation',
      'Local codes and guidelines discussion',
      'Contractor finding guidance',
      'Permit application guidance',
      'Space design consultation',
      'Airbnb listing setup assistance',
      'Unlimited online chat support',
      '1 hour phone consultation included'
    ],
    popular: false,
    color: 'bg-gray-100',
    isFree: false
  },
  {
    name: 'Gold',
    tagline: 'Full-Service Solution',
    price: '$999',
    priceNote: 'Complete setup package',
    additionalRate: '$199/hour',
    additionalNote: 'Additional training',
    phoneHours: '5 hours included',
    features: [
      'Everything in Silver, plus:',
      'Permit application assistance',
      'Contractor sourcing and vetting',
      'Price evaluation and negotiations',
      'Complete space setup coordination',
      'Furniture sourcing and selection',
      'Multi-platform listing setup',
      'Dynamic pricing strategy setup',
      'Professional listing optimization',
      '5 hours phone/video training included'
    ],
    popular: false,
    color: 'bg-gradient-to-br from-[#FF6B6B] to-[#ff5252]',
    isFree: false
  },
  {
    name: 'Platinum',
    tagline: 'Complete Turnkey Service',
    price: '$2,999',
    priceNote: 'All-inclusive premium package',
    additionalRate: 'Unlimited',
    additionalNote: 'First 3 months',
    phoneHours: 'Unlimited for 3 months',
    features: [
      'Everything in Gold, plus:',
      'Detailed space evaluation and analysis',
      'Professional space design service',
      'Complete permit application handling',
      'Contractor sourcing and hiring',
      'Full project supervision on your behalf',
      'Furniture purchasing assistance',
      'Complete Airbnb property setup',
      'Live listing launch management',
      'Algorithm-optimized pricing strategy',
      'Unlimited phone access for 3 months'
    ],
    popular: true,
    color: 'bg-gradient-to-br from-[#1A2332] to-[#2a3442]',
    isFree: false
  }
];

export default function PricingPlans() {
  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-[#1A2332] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Service Packages
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Start with a free consultation to see how we can help, then choose the package that best fits your needs and budget.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`relative rounded-3xl overflow-hidden ${
                plan.popular ? 'shadow-2xl lg:scale-105' : 'shadow-lg'
              } transition-all duration-300 hover:shadow-2xl ${
                plan.isFree ? 'border-4 border-[#FF6B6B]' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-6 right-6 z-10">
                  <span className="inline-block px-4 py-2 bg-white text-[#FF6B6B] text-sm font-bold rounded-full shadow-lg">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {plan.isFree && (
                <div className="absolute top-6 right-6 z-10">
                  <span className="inline-block px-4 py-2 bg-white text-[#FF6B6B] text-sm font-bold rounded-full shadow-lg">
                    START HERE
                  </span>
                </div>
              )}

              <div className={`${plan.color} ${plan.popular || plan.name === 'Gold' || plan.isFree ? 'text-white' : 'text-[#1A2332]'} p-8`}>
                <h3 className="text-3xl font-bold mb-2">
                  {plan.name}
                </h3>
                <p className={`text-lg mb-6 ${plan.popular || plan.name === 'Gold' || plan.isFree ? 'text-white/90' : 'text-gray-600'}`}>
                  {plan.tagline}
                </p>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className={`${plan.isFree ? 'text-4xl' : 'text-5xl'} font-bold`}>{plan.price}</span>
                  </div>
                  <p className={`text-sm ${plan.popular || plan.name === 'Gold' || plan.isFree ? 'text-white/80' : 'text-gray-500'}`}>
                    {plan.priceNote}
                  </p>
                </div>

                <div className={`${plan.popular || plan.name === 'Gold' || plan.isFree ? 'bg-white/20' : 'bg-white'} rounded-xl p-4 mb-6`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-semibold ${plan.popular || plan.name === 'Gold' || plan.isFree ? 'text-white' : 'text-[#1A2332]'}`}>
                      {plan.isFree ? 'Call Duration:' : 'Phone Support:'}
                    </span>
                    <span className={`font-bold text-sm ${plan.popular || plan.name === 'Gold' || plan.isFree ? 'text-white' : 'text-[#FF6B6B]'}`}>
                      {plan.phoneHours}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${plan.popular || plan.name === 'Gold' || plan.isFree ? 'text-white/80' : 'text-gray-600'}`}>
                      {plan.isFree ? 'Commitment:' : 'Additional:'}
                    </span>
                    <span className={`text-sm font-semibold ${plan.popular || plan.name === 'Gold' || plan.isFree ? 'text-white' : 'text-[#1A2332]'}`}>
                      {plan.additionalRate}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8">
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className={`text-gray-700 ${feature.includes('Everything') ? 'font-semibold text-[#1A2332]' : ''}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link 
                  to="/contact"
                  className={`block text-center px-8 py-4 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    plan.isFree
                      ? 'bg-[#FF6B6B] text-white hover:bg-[#ff5252] shadow-lg hover:shadow-xl'
                      : plan.popular 
                      ? 'bg-[#1A2332] text-white hover:bg-[#2a3442] shadow-lg hover:shadow-xl' 
                      : plan.name === 'Gold'
                      ? 'bg-[#FF6B6B] text-white hover:bg-[#ff5252] shadow-lg hover:shadow-xl'
                      : 'bg-[#1A2332] text-white hover:bg-[#2a3442]'
                  }`}
                >
                  {plan.isFree ? 'Schedule Free Call' : `Get Started with ${plan.name}`}
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Need a custom solution? We can create a tailored package for your specific needs.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center text-[#FF6B6B] font-semibold hover:gap-2 transition-all cursor-pointer"
          >
            Contact us for custom pricing
            <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
